<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  pullDistance: number;
  isTriggered: boolean;
  isReloading: boolean;
  /** Limite visual maximo do pull em px (mesmo valor do composable). */
  maxDisplay?: number;
}

const props = withDefaults(defineProps<Props>(), {
  maxDisplay: 80,
});

const { topBarHeight, headerHeight } = useLayout();

// Posiciona logo abaixo do topbar + header. Como o headerWrapper e sticky
// (top-0) no `layouts/default.vue`, esse offset coincide com o limite
// inferior do header em qualquer scroll position. No iOS o headerHeight ja
// inclui a safe area (ver components/header/Header.vue), entao esse calculo
// continua valido em aparelhos com notch/Dynamic Island.
const topOffset = computed<number>(() => {
  return topBarHeight.value + headerHeight.value + 8;
});

const ratio = computed<number>(() => {
  return Math.min(props.pullDistance / props.maxDisplay, 1);
});

// Opacidade sobe rapido (0 → 1 ate 60% do pull), pra dar feedback visivel
// logo no inicio do gesto sem esperar chegar perto do trigger.
const opacity = computed<number>(() => {
  return Math.min(ratio.value * 1.6, 1);
});

// Translacao adicional pra dar a sensacao de "puxado" — alem do offset
// fixo abaixo do header, o circulo desce um pouco conforme o usuario puxa.
const translateY = computed<number>(() => {
  return ratio.value * 28;
});

// Rotacao manual durante o pull. Quando recarregando, CSS animation toma
// conta e essa rotacao fica congelada (estilo inline e sobrescrito).
const rotation = computed<number>(() => {
  return ratio.value * 270;
});
</script>

<template>
  <div
    aria-hidden="true"
    :class="$style.wrapper"
    :style="{
      top: `${topOffset}px`,
      transform: `translate(-50%, ${translateY}px)`,
      opacity,
    }"
  >
    <div :class="[$style.bubble, { [$style.triggered]: isTriggered }]">
      <div
        :class="[$style.spinner, { [$style.spinning]: isReloading }]"
        :style="
          isReloading ? undefined : { transform: `rotate(${rotation}deg)` }
        "
      />
    </div>
  </div>
</template>

<style module lang="postcss">
.wrapper {
  @apply pointer-events-none fixed left-1/2 z-[80] select-none;
  transition: opacity 160ms ease-out;
}

.bubble {
  @apply flex items-center justify-center rounded-full;
  width: 36px;
  height: 36px;
  background: var(--colors-bg-secondary, #0a1e33);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  transition: border-color 150ms ease-out;
}

.bubble.triggered {
  border-color: rgba(255, 255, 255, 0.4);
}

.spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: #ffffff;
}

.spinner.spinning {
  animation: pullSpin 750ms linear infinite;
}

@keyframes pullSpin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
